import React from "react";
import { Edit, LinearProgress, usePermissions, useTranslate } from "react-admin";
import { isInArray } from "utils/general";

import WizardForm from "../../../components/forms/WizardForm";
import { useTerminalsContext } from "../../../contexts/TerminalsProvider";
import { DeviationStatus, allowedEditRolesByStatus } from "../../../entities/DeviationStatus";
import { DeviationWizardToolbar } from "../DeviationWizardToolbar";
import {
	Form1,
	Form2,
	Form3,
	Form4,
	Form5,
	FormAtCustomer,
	FormAtManagement,
	FormAtManager,
	FormFirstAtManager,
} from "../forms";
import { useGetOrder, useOnSubmit } from "../requests_tools";

const firstStepNumberByStatus = {
	[DeviationStatus.OPEN]: 0,
	[DeviationStatus.FIRST_AT_MANAGER]: 5,
	[DeviationStatus.AT_CUSTOMER]: 5,
	[DeviationStatus.AT_MANAGER]: 5,
	[DeviationStatus.AT_MANAGEMENT]: 6,
};
const enableStatuses0 = [DeviationStatus.OPEN];
const enableStatuses1 = [DeviationStatus.OPEN, DeviationStatus.AT_MANAGEMENT];
const enableStatuses2 = [DeviationStatus.AT_MANAGER, DeviationStatus.AT_MANAGEMENT];
const enableStatuses3 = [DeviationStatus.AT_MANAGEMENT];

const EditForm = ({ save, ...props }) => {
	const { permissions } = usePermissions();
	const { data: orderRecord, loaded } = useGetOrder(props.record.order_id);
	const { data: terminals, loading } = useTerminalsContext();

	const onSubmit = useOnSubmit(`deviations/${props.record.id}`, "PUT", props.basePath, orderRecord);

	if (!loaded || loading) return <LinearProgress />;
	const terminalRecord = terminals[orderRecord.terminal_id];

	const status = props.record.status;
	const canEdit = allowedEditRolesByStatus[status]?.has(permissions.role);
	const formDisabled = !canEdit;

	const enableFirstAtManager = status === DeviationStatus.FIRST_AT_MANAGER && !formDisabled;
	const enableAtCustomer =
		status === DeviationStatus.AT_CUSTOMER && status !== DeviationStatus.CLOSE_NO_ACTION;
	const enableAtManager =
		status >= DeviationStatus.AT_MANAGER && status !== DeviationStatus.CLOSE_NO_ACTION;
	const enableAtManagement =
		status >= DeviationStatus.AT_MANAGEMENT && status !== DeviationStatus.CLOSE_NO_ACTION;

	const defaultStepNumber = formDisabled ? 0 : firstStepNumberByStatus[status] || 0;

	return (
		<WizardForm
			save={onSubmit}
			saveDisabled={formDisabled}
			toolbarComponent={DeviationWizardToolbar}
			defaultStepNumber={defaultStepNumber}
			{...props}
		>
			<Form1
				label="Category"
				isEdit={true}
				disabled={formDisabled || !isInArray(enableStatuses0, status)}
				terminalRecord={terminalRecord}
			/>
			<Form2
				label="Description"
				orderRecord={orderRecord}
				disabled={formDisabled || !isInArray(enableStatuses1, status)}
			/>
			<Form3
				label="Details"
				orderRecord={orderRecord}
				disabled={formDisabled || !isInArray(enableStatuses1, status)}
			/>
			<Form4
				label="Notification"
				orderRecord={orderRecord}
				terminalRecord={terminalRecord}
				disabled={formDisabled || !isInArray(enableStatuses1, status)}
			/>
			<Form5
				label="Attachment"
				status={status}
				isEdit={true}
				disabled={formDisabled || !isInArray(enableStatuses1, status)}
			/>
			{enableFirstAtManager && (
				<FormFirstAtManager
					label="Manager action"
					status={status}
					orderRecord={orderRecord}
					disabled={formDisabled}
				/>
			)}
			{enableAtCustomer && (
				<FormAtCustomer
					label="At customer"
					status={status}
					orderRecord={orderRecord}
					disabled={formDisabled}
				/>
			)}
			{enableAtManager && (
				<FormAtManager
					label="At manager"
					status={status}
					orderRecord={orderRecord}
					disabled={formDisabled || !isInArray(enableStatuses2, status)}
				/>
			)}
			{enableAtManagement && (
				<FormAtManagement
					label="Claim"
					status={status}
					orderRecord={orderRecord}
					disabled={formDisabled || !isInArray(enableStatuses3, status)}
				/>
			)}
		</WizardForm>
	);
};

export const DeviationEdit = (props) => {
	const translate = useTranslate();

	const mainTitle = translate(`resources.${props.resource}.name`);
	const title = `${translate("ra.action.edit")} ${mainTitle}`;

	return (
		<Edit {...props} mutationMode={"pessimistic"} title={title}>
			<EditForm />
		</Edit>
	);
};
